/**
 * What a vendor's clock time is on other stores' clocks, for the admin Price
 * clocks page: Sony's 00:00 Pacific is 03:00 for a New York store.
 */

function zonedParts(instantMs: number, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instantMs));
  const out: Record<string, number> = {};
  for (const p of parts) if (p.type !== 'literal') out[p.type] = Number(p.value);
  return { ...out, hour: out.hour === 24 ? 0 : out.hour } as Record<'year' | 'month' | 'day' | 'hour' | 'minute' | 'second', number>;
}

function offsetMs(instantMs: number, timeZone: string): number {
  const p = zonedParts(instantMs, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instantMs / 1000) * 1000;
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * `time` ('HH:MM' or 'HH:MM:SS') on `date` ('YYYY-MM-DD') in `fromZone`, as
 * 'HH:MM' in `toZone`, with '(-1 day)' / '(+1 day)' when the date differs.
 */
export function wallClockIn(date: string, time: string, fromZone: string, toZone: string): string {
  const [y, mo, d] = date.split('-').map(Number);
  const [h, mi, s = 0] = time.split(':').map(Number);
  const target = Date.UTC(y, mo - 1, d, h, mi, s);
  let instant = target - offsetMs(target, fromZone);
  instant = target - offsetMs(instant, fromZone);
  const p = zonedParts(instant, toZone);
  const shift = Math.round((Date.UTC(p.year, p.month - 1, p.day) - Date.UTC(y, mo - 1, d)) / 86_400_000);
  return `${pad(p.hour)}:${pad(p.minute)}${shift === 0 ? '' : ` (${shift > 0 ? '+' : ''}${shift} day)`}`;
}

/** Short zone label without daylight wording: 'PT', 'ET'. */
export function zoneShortName(timeZone: string, at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'shortGeneric' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value || timeZone;
  } catch {
    return timeZone;
  }
}

/** Today in `timeZone`, 'YYYY-MM-DD'. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  const p = zonedParts(now.getTime(), timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}
